import type { TFunction } from "i18next";
import { useRef, type RefObject, useMemo, useEffect } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputMask from "../../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import type { U3120CustomerContactHandler } from "./U3120CustomerContactHandlers";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import { u2000ExtractAutoCompleteResults } from "../../../../../u2000Core/utils/u2000ExtractAutoCompleteResults";
import { useTranslation } from "react-i18next";
import { doFindContactTypes, type U3120CustomerContact, U3120ContactPhoneType, luHandleAppiError } from "../../../../U3120Service";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";

// Component props interface
interface U3120CustomerContactDlgProps {
  u3120CustomerContactHandler: U3120CustomerContactHandler;
  t: TFunction;
  NS: string;
}

// Options pour le type de téléphone (enum OFFICE, HOME, CELLPHONE)
function getPhoneTypeOptions(t: TFunction, NS: string) {
  return [
    { value: U3120ContactPhoneType.OFFICE, label: t("lblPhoneTypeOffice", { ns: NS }) },
    { value: U3120ContactPhoneType.HOME, label: t("lblPhoneTypeHome", { ns: NS }) },
    { value: U3120ContactPhoneType.CELLPHONE, label: t("lblPhoneTypeCellphone", { ns: NS }) },
  ];
}

// Dialog component for editing customer contacts
function U3120CustomerContactEditDlg({ u3120CustomerContactHandler, t, NS }: U3120CustomerContactDlgProps) {
  const { formStateHandlers } = u3120CustomerContactHandler;
  const { i18n } = useTranslation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // Ref for the contact name input to be used as default focus
  const contactNameRef = useRef<HTMLInputElement>(null);

  const phoneTypeOptions = useMemo(() => getPhoneTypeOptions(t, NS), [t, NS]);

  const contactData = formStateHandlers.getData();
  const phone1Type = contactData?.phone1Type;
  const phone2Type = contactData?.phone2Type;

  // Quand le type de téléphone n'est pas OFFICE, mettre l'extension à null (éditable seulement si Office)
  useEffect(() => {
    const data = formStateHandlers.getData();
    if (!data) return;
    const next: U3120CustomerContact = { ...data };
    let updated = false;
    if (data.phone1Type !== U3120ContactPhoneType.OFFICE && (data.phone1Extension != null && data.phone1Extension !== "")) {
      next.phone1Extension = null;
      updated = true;
    }
    if (data.phone2Type !== U3120ContactPhoneType.OFFICE && (data.phone2Extension != null && data.phone2Extension !== "")) {
      next.phone2Extension = null;
      updated = true;
    }
    if (updated) {
      formStateHandlers.setData(next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- on purpose: clear extensions only when type changes
  }, [phone1Type, phone2Type]);

  // Calcul dynamique des resultFields selon la langue pour les titres
  const contactTypeeResultFields = useMemo(() => {
    const language = i18n.language;
    if (language === "fr") return ["contactTypeNameFr"];
    if (language === "en") return ["contactTypeNameEn"];
    return ["contactTypeName"];
  }, [i18n.language]);

  // Fonction de recherche de types de contact
  const onSearchContactType = async (criterias: { fieldName: string; value: any }[]) => {
    try {
      const response = await doFindContactTypes(u2000AxiosAuthenticated, criterias);
      return u2000ExtractAutoCompleteResults<Record<string, any>>(response.data);
    } catch (error: any) {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return { results: [], totalCount: 0, truncated: false };
    }
  };

  // Fonction d'affichage du type de contact
  const displayContactType = (item: any) => {
    if (!item) return "";
    const language = i18n.language;
    if (language === "fr" && item.contactTypeNameFr) {
      return item.contactTypeNameFr;
    }
    if (language === "en" && item.contactTypeNameEn) {
      return item.contactTypeNameEn;
    }
    return item.contactTypeCode || "";
  };

  // Fonction d'extraction des données du type de contact
  const extractContactTypeData = (result: any) => {
    if (result) {
      return [
        { fieldName: "contactType.id", value: result.id },
        { fieldName: "contactType.contactTypeCode", value: result.contactTypeCode || "" },
        { fieldName: "contactType.contactTypeNameFr", value: result.contactTypeNameFr || "" },
        { fieldName: "contactType.contactTypeNameEn", value: result.contactTypeNameEn || "" },
      ];
    } else {
      return [
        { fieldName: "contactType.id", value: null },
        { fieldName: "contactType.contactTypeCode", value: null },
        { fieldName: "contactType.contactTypeNameFr", value: null },
        { fieldName: "contactType.contactTypeNameEn", value: null },
      ];
    }
  };

  // Unified handler that manages Save and Cancel differently
  const handleClose = (action: "save" | "cancel") => {
    const isReadMode = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;

    // En mode lecture, fermer directement sans validation
    if (isReadMode) {
      u3120CustomerContactHandler.onDlgClose("cancel");
      return;
    }

    if (action === "cancel") {
      // Temporarily disable onBlur validation to prevent unwanted validation on cancel
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      // Close dialog immediately without validation
      u3120CustomerContactHandler.onDlgClose("cancel");

      // Restore original onBlur handler after dialog closes
      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      // Normal save flow with validation
      u3120CustomerContactHandler.onDlgClose("save");
    }
  };

  // Vérifier si on est en mode lecture seule
  const isReadMode = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  // render
  return (
    <U2000SaveCancelDialog
      isOpen={u3120CustomerContactHandler.isDialogOpen}
      onClose={handleClose}
      title={t("lblCustomerContact", { ns: NS })}
      isValid={true}
      readOnly={isReadMode}
      defaultFocusRef={contactNameRef as RefObject<HTMLElement>}
    >
      <div className="flex flex-col gap-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <U2000InputText
              fieldName="contactName"
              label={t("lblContactName", { ns: NS })}
              formStateHandlers={formStateHandlers}
              inputRef={contactNameRef as React.RefObject<HTMLInputElement>}
              required={true}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <U2000InputText fieldName="title" label={t("lblTitle", { ns: NS })} formStateHandlers={formStateHandlers} />
          </div>
        </div>

        <div>
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers}
            fieldName="contactType"
            mainFieldName="contactTypeCode"
            label={t("lblContactType", { ns: NS })}
            onSearch={onSearchContactType}
            displayFn={displayContactType}
            overrideExtractDataFn={extractContactTypeData}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={contactTypeeResultFields}
            required={true}
          />
        </div>

        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <div>
            <U2000InputMask fieldName="phone1" label={t("lblPhone1", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} />
          </div>
          <div>
            <U2000ComboBox
              formStateHandlers={formStateHandlers}
              fieldName="phone1Type"
              label={t("lblPhoneType", { ns: NS })}
              data={phoneTypeOptions}
              keyField="value"
              descField="label"
              getValue={(item) => item.value}
              findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
            />
          </div>
          <div className="min-w-[100px]">
            <U2000InputText
              fieldName="phone1Extension"
              label={t("lblPhone1Extension", { ns: NS })}
              formStateHandlers={formStateHandlers}
              readonly={phone1Type !== U3120ContactPhoneType.OFFICE}
            />
          </div>
        </div>

        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <div>
            <U2000InputMask fieldName="phone2" label={t("lblPhone2", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} />
          </div>
          <div>
            <U2000ComboBox
              formStateHandlers={formStateHandlers}
              fieldName="phone2Type"
              label={t("lblPhoneType", { ns: NS })}
              data={phoneTypeOptions}
              keyField="value"
              descField="label"
              getValue={(item) => item.value}
              findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
            />
          </div>
          <div className="min-w-[100px]">
            <U2000InputText
              fieldName="phone2Extension"
              label={t("lblPhone2Extension", { ns: NS })}
              formStateHandlers={formStateHandlers}
              readonly={phone2Type !== U3120ContactPhoneType.OFFICE}
            />
          </div>
        </div>

        <div>
          <U2000InputText fieldName="email" label={t("lblEmail", { ns: NS })} formStateHandlers={formStateHandlers} />
        </div>

        <div>
          <U2000InputText fieldName="notes" label={t("lblNotes", { ns: NS })} formStateHandlers={formStateHandlers} />
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3120CustomerContactEditDlg;
